import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function AuthProvider({ children }) {
  const [user,       setUser]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [blockedMsg, setBlockedMsg] = useState(null);
  // When blocked, all pending toasts / requests should be silenced
  const isBlockedRef = useRef(false);

  const logout = useCallback((msg) => {
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    if (msg) {
      isBlockedRef.current = true;
      setBlockedMsg(msg);
    }
  }, []);

  // Expose a helper so components can check if they should show toasts
  const isBlocked = () => isBlockedRef.current;

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/api/auth/me')
        .then(res => { setUser(res.data); isBlockedRef.current = false; })
        .catch(err => {
          if (err.response?.status === 403 &&
             (err.response.data?.blocked || err.response.data?.error?.includes('blocked'))) {
            logout(err.response.data.error ||
              'Your organization has been blocked. Please contact the administrator.');
          } else {
            sessionStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [logout]);

  // Global axios interceptor — immediately logs out on any 403 blocked
  // Suppresses further error toasts by setting isBlockedRef
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        const data = err.response?.data;
        if (err.response?.status === 403 &&
           (data?.blocked || data?.error?.includes('blocked') || data?.error?.includes('organization has been blocked'))) {
          // Only trigger once
          if (!isBlockedRef.current) {
            logout(data.error ||
              'Your organization has been blocked by the administrator. Please contact support.');
          }
          // Swallow the error so no toast fires from the calling code
          return new Promise(() => {}); // never resolves — silences .catch() in components
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    sessionStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    isBlockedRef.current = false;
    setUser(res.data.user);
    setBlockedMsg(null);
    return res.data.user;
  };

  const register = async (data) => {
    const res = await axios.post('/api/auth/register', data);
    sessionStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setUser(res.data.user);
    return res.data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, blockedMsg, setBlockedMsg, isBlocked }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
